import React from "react";
import { KeyTerm, ImportantNote, Mnemonic } from "../note-components";
import type { UnitNotes } from "../types";

const humanGeographyUnit5Notes: UnitNotes = {
  unitId: "unit-5",
  unitName: "Unit 5: Agriculture and Rural Land-Use Patterns and Processes",
  examWeight: "12–17%",
  focus: "Understanding agricultural systems, their spatial patterns, environmental impacts, and role in global food systems.",
  sections: [
    {
      id: "topic-5-1",
      title: "Topic 5.1: Introduction to Agriculture",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Connection Between Physical Geography and Agriculture</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Agriculture is the deliberate modification of Earth's surface through cultivation of plants and rearing of animals to obtain sustenance or economic gain.
            </p>

            <div className="space-y-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">🌡️ Climate</h4>
                <p className="text-sm text-slate-700 mt-1">Determines <em>what</em> can grow where.</p>
                <ul className="text-xs text-slate-600 space-y-1 list-disc pl-4 mt-2">
                  <li><strong>Tropical:</strong> Plantation crops (coffee, sugar), shifting cultivation</li>
                  <li><strong>Dry/Arid:</strong> Pastoral nomadism, ranching</li>
                  <li><strong>Mediterranean:</strong> Specialty crops (olives, grapes, figs)</li>
                </ul>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">⛰️ Topography</h4>
                <p className="text-sm text-slate-700 mt-1">Flat lands preferred for large-scale crop production; mountains require terrace farming.</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">🌱 Soil</h4>
                <p className="text-sm text-slate-700 mt-1">Nutrient-rich soil (volcanic, river deltas) supports intensive farming.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Intensive vs. Extensive Farming</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">🔥 Intensive Agriculture</h4>
                <p className="text-sm text-slate-700 mt-1">High inputs of labor, capital, or fertilizer per unit of land.</p>
                <ul className="text-xs text-slate-600 space-y-1 list-disc pl-4 mt-2">
                  <li>Small plots of land</li>
                  <li>Market Gardening, Plantation, Rice Paddies</li>
                </ul>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">🌾 Extensive Agriculture</h4>
                <p className="text-sm text-slate-700 mt-1">Low inputs of labor and capital relative to land area.</p>
                <ul className="text-xs text-slate-600 space-y-1 list-disc pl-4 mt-2">
                  <li>Large plots of land</li>
                  <li>Shifting Cultivation, Pastoral Nomadism, Ranching</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Specific Agricultural Practices</h3>
            <div className="space-y-3">
              <KeyTerm term="Market Gardening" definition="Production of fruits, vegetables, and flowers sold directly to consumers or processors. Found near urban centers." />
              <KeyTerm term="Plantation Agriculture" definition="Large farm in tropical/subtropical climate specializing in one or two crops for sale. Legacy of colonialism." />
              <KeyTerm term="Mixed Crop and Livestock" definition="Integration of crops and livestock. Most crops fed to animals; animals provide manure for fertilizer." />
              <KeyTerm term="Shifting Cultivation" definition="Slash-and-Burn farming. Clear land by burning vegetation, farm for a few years, then leave fallow to recover. Found in tropical rainforests." />
              <KeyTerm term="Pastoral Nomadism" definition="Herding of domesticated animals in dry climates where planting crops is impossible. Herders migrate for water and pasture." />
              <KeyTerm term="Ranching" definition="Commercial grazing of livestock over extensive area. Adapted to semi-arid or arid land." />
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-5-2",
      title: "Topic 5.2: Settlement Patterns and Survey Methods",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Rural Settlement Patterns</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              How people group themselves in rural areas usually revolves around resources (water) and culture.
            </p>

            <div className="space-y-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">🏘️ Clustered (Nucleated) Settlement</h4>
                <p className="text-sm text-slate-700 mt-1">Houses and farm buildings grouped closely together with fields surrounding the settlement.</p>
                <p className="text-xs text-slate-600 mt-2"><strong>Purpose:</strong> Defense, shared resources, social cohesion</p>
                <p className="text-xs text-slate-600"><strong>Location:</strong> Historical Europe, Sub-Saharan Africa</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">🏠 Dispersed Settlement</h4>
                <p className="text-sm text-slate-700 mt-1">Farmers live on individual farms isolated from neighbors.</p>
                <p className="text-xs text-slate-600 mt-2"><strong>Purpose:</strong> Independence, large-scale mechanized farming</p>
                <p className="text-xs text-slate-600"><strong>Location:</strong> North American Midwest/Great Plains</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">📍 Linear Settlement</h4>
                <p className="text-sm text-slate-700 mt-1">Buildings clustered along a road, river, or dike to facilitate communications/transport.</p>
                <p className="text-xs text-slate-600 mt-2"><strong>Location:</strong> Along the St. Lawrence River in Canada</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Rural Survey Methods</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              How land boundaries are defined and recorded.
            </p>

            <div className="space-y-3">
              <KeyTerm term="Metes and Bounds" definition="Uses physical features (trees, rocks, streams) to define irregular parcels of land. Region: England and Colonial America (East Coast)." />
              <KeyTerm term="Township and Range" definition="Rigid grid system dividing land into 6x6 mile square townships, then into 1-mile sections. Designed by Thomas Jefferson. Region: US Midwest and West." />
              <KeyTerm term="Long Lot" definition="Divides land into narrow parcels stretching back from rivers, roads, or canals. Ensures every farmer has access to transportation/water. Region: French settled areas (Quebec, Louisiana)." />
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-5-3",
      title: "Topic 5.3: Agricultural Origins and Diffusions",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The First Agricultural Revolution (Neolithic Revolution)</h3>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <p className="text-sm text-slate-700">Occurred approx. 10,000 years ago. The shift from hunting and gathering to settled farming (domestication of plants and animals). This led to the first permanent settlements and population growth.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Hearths of Domestication</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Agriculture originated independently in several hearths:
            </p>

            <div className="grid md:grid-cols-2 gap-3">
              <KeyTerm term="Fertile Crescent" definition="Wheat, barley, oats, sheep, goats, cattle." />
              <KeyTerm term="Southeast Asia" definition="Mango, taro, coconut." />
              <KeyTerm term="East Asia" definition="Rice, soybeans." />
              <KeyTerm term="Sub-Saharan Africa" definition="Yams, sorghum, coffee." />
              <KeyTerm term="Mesoamerica" definition="Maize (corn), squash, beans." />
              <KeyTerm term="Andes" definition="Potatoes." />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Global Diffusion & The Columbian Exchange</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              After 1492, the Columbian Exchange facilitated the global diffusion of plants and animals between the New World (Americas) and the Old World (Europe/Africa/Asia).
            </p>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">🌍 To the Americas</h4>
                <p className="text-sm text-slate-700 mt-1">Wheat, cattle, horses, sheep, pigs, sugar, rice</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">🌎 To Europe/Africa/Asia</h4>
                <p className="text-sm text-slate-700 mt-1">Maize (corn), potatoes, tobacco, cacao, peanuts, tomatoes</p>
              </div>
            </div>
            <ImportantNote>
              <strong>Impact:</strong> Population boom in Europe/Asia due to calorically dense crops (potatoes/corn).
            </ImportantNote>
          </section>
        </div>
      )
    },
    {
      id: "topic-5-4",
      title: "Topic 5.4: The Second Agricultural Revolution",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Context and Timing</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Occurred roughly 1700s–1900 in Great Britain, Denmark, and other European countries. It coincided with and helped drive the <strong>Industrial Revolution</strong>.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Technological Innovations</h3>
            <div className="space-y-3">
              <KeyTerm term="Mechanization" definition="The steel plow and mechanical reaper replaced human labor, increasing efficiency." />
              <KeyTerm term="Enclosure Acts" definition="Laws in England allowing landowners to fence off common lands. Consolidated land into larger, efficient farms but forced small peasant farmers off the land." />
              <KeyTerm term="Crop Rotation (Norfolk System)" definition="Planting different crops in the same field in sequential seasons to restore soil nutrients without leaving land fallow." />
              <KeyTerm term="Transportation" definition="Railroads allowed food to be shipped further without spoiling." />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Impacts</h3>
            <div className="grid md:grid-cols-3 gap-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">🍎 Diet/Health</h4>
                <p className="text-sm text-slate-700 mt-1">Increased food production led to better diets and longer life expectancy</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">📊 Demographics</h4>
                <p className="text-sm text-slate-700 mt-1">Pushed the world into Stage 2 of the DTM (population explosion)</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">🏙️ Urbanization</h4>
                <p className="text-sm text-slate-700 mt-1">Displaced farmers moved to cities to provide labor for factories</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-5-5",
      title: "Topic 5.5: The Green Revolution (Third Agricultural Revolution)",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Context</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Began in the mid-20th century (1950s–1970s). Research aimed at reducing hunger in Less Developed Countries (LDCs), specifically Mexico, India, and Pakistan. Key figure: <strong>Norman Borlaug</strong>.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Characterized By</h3>
            <div className="space-y-3">
              <KeyTerm term="High-Yield Varieties (HYVs)" definition="Genetically cross-breeding seeds (wheat, rice, corn) to be shorter, hardier, and produce more grain." />
              <KeyTerm term="Chemical Inputs" definition="Heavy reliance on synthetic fertilizers and pesticides." />
              <KeyTerm term="Mechanization & Irrigation" definition="Increased use of machinery and water." />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Consequences</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">✅ Positive</h4>
                <ul className="text-xs text-slate-600 space-y-1 list-disc pl-4 mt-2">
                  <li>Prevented massive famines in Asia/Latin America</li>
                  <li>India became self-sufficient in grain</li>
                  <li>Lowered global food prices</li>
                </ul>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">❌ Negative</h4>
                <ul className="text-xs text-slate-600 space-y-1 list-disc pl-4 mt-2">
                  <li>Soil degradation, chemical runoff, biodiversity loss</li>
                  <li>Small farmers couldn't afford expensive seeds/chemicals</li>
                  <li>Technologies marketed to men, marginalizing female farmers</li>
                  <li>Africa largely bypassed</li>
                </ul>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-5-6",
      title: "Topic 5.6: Agricultural Production Regions",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Subsistence vs. Commercial</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">🏡 Subsistence</h4>
                <p className="text-sm text-slate-700 mt-1">Farming for consumption by the farmer's family. Found mostly in LDCs.</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">💰 Commercial</h4>
                <p className="text-sm text-slate-700 mt-1">Farming for profit/off-farm sale. Found mostly in MDCs.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Bid-Rent Theory</h3>
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <p className="text-sm text-slate-700">Land price is highest near the market (city) and decreases as you move away.</p>
              <ul className="text-xs text-slate-600 space-y-1 list-disc pl-4 mt-2">
                <li><strong>Intensive practices</strong> (Dairy, Fruit/Veg) are located near the market because they can afford high land costs</li>
                <li><strong>Extensive practices</strong> (Grain, Ranching) are located far from the market because they require vast land</li>
              </ul>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Monocropping/Monoculture</h3>
            <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
              <p className="text-sm text-slate-700">Growing a single crop year after year on the same land. Common in commercial farming (efficiency) but risky regarding pests and soil depletion.</p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-5-7",
      title: "Topic 5.7: Spatial Organization of Agriculture",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Agribusiness</h3>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <p className="text-sm text-slate-700">Agriculture is no longer just "farming"; it is a system of commercial agriculture characterized by the integration of different steps in the food-processing industry.</p>
              <p className="text-sm text-slate-700 mt-2"><strong>Includes:</strong> Farm production → Processing → Packaging → Storing → Distributing → Retailing</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Economies of Scale</h3>
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <p className="text-sm text-slate-700">The concept that as the scale of production increases, the cost per unit decreases.</p>
              <p className="text-sm text-slate-700 mt-2"><strong>Impact:</strong> Large corporate farms (agribusiness) can produce food cheaper than small family farms, causing a decline in the number of family farms</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Commodity Chains</h3>
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <p className="text-sm text-slate-700">A process used by firms to gather resources, transform them into goods or commodities, and finally, distribute them to consumers.</p>
              <p className="text-sm text-slate-700 mt-2"><strong>Example:</strong> Corn grown in Iowa → shipped to processing plant → turned into High Fructose Corn Syrup → sold to soda company → bottled → sold in store</p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-5-8",
      title: "Topic 5.8: Von Thünen Model",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Premise</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Created by Johann Heinrich von Thünen in 1826. Explains rural land use based on <strong>Transportation Costs</strong> (weight and perishability).
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Rings (Center is the Market/City)</h3>
            <div className="space-y-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Ring 1: Horticulture and Dairying</h4>
                <p className="text-sm text-slate-700 mt-1">Products are perishable (milk, strawberries) and difficult to transport. Land is expensive, requiring intensive land use.</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">Ring 2: Forestry</h4>
                <p className="text-sm text-slate-700 mt-1">Wood is heavy and expensive to transport; historically used for fuel and building.</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">Ring 3: Grains/Field Crops</h4>
                <p className="text-sm text-slate-700 mt-1">Crops are non-perishable and lighter to transport. Land is cheaper, allowing extensive farming.</p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">Ring 4: Ranching/Livestock</h4>
                <p className="text-sm text-slate-700 mt-1">Animals are self-transporting (they can walk to market), requiring vast amounts of cheap land.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-5-9",
      title: "Topic 5.9: The Global System of Agriculture",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Global Supply Chains</h3>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <p className="text-sm text-slate-700">Food is part of a global market. Products grown in the Southern Hemisphere are sold in the Northern Hemisphere (especially during winter).</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Dependency and Export Crops</h3>
            <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
              <p className="text-sm text-slate-700">Many LDCs (periphery countries) have economies dependent on exporting <strong>luxury crops</strong> (coffee, tea, cacao, sugar, bananas) to MDCs (core countries).</p>
              <p className="text-sm text-slate-700 mt-2"><strong>Problem:</strong> Farmers grow cash crops for export instead of food crops for the local population, potentially leading to local food insecurity.</p>
              <p className="text-sm text-slate-700 mt-2"><strong>Neocolonialism:</strong> Transnational Corporations (TNCs) based in MDCs often control the production and prices in LDCs.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Political Influences</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <KeyTerm term="Subsidies" definition="Governments pay farmers to produce certain crops (like corn in the US) to keep prices low or protect domestic farmers." />
              <KeyTerm term="Infrastructure" definition="Reliable roads and ports are required to participate in the global market." />
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-5-10",
      title: "Topic 5.10: Consequences of Agricultural Practices",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Environmental Impacts</h3>
            <div className="space-y-3">
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">🌊 Pollution</h4>
                <p className="text-sm text-slate-700 mt-1">Runoff from fertilizers (nitrogen/phosphorus) causes algae blooms and dead zones in oceans/lakes. Pesticides harm non-target species (bees).</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">🏜️ Desertification</h4>
                <p className="text-sm text-slate-700 mt-1">Human actions (overgrazing, deforestation) cause fertile land to turn into desert (common in the Sahel region of Africa).</p>
              </div>
              <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                <h4 className="font-bold text-yellow-800">🧂 Soil Salinization</h4>
                <p className="text-sm text-slate-700 mt-1">Excessive irrigation in dry areas evaporates, leaving salt behind in the soil, which eventually makes the soil toxic to plants.</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">🌳 Deforestation</h4>
                <p className="text-sm text-slate-700 mt-1">Clearing forests for farmland (e.g., palm oil in SE Asia, cattle in Amazon).</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Landscape Alterations</h3>
            <div className="space-y-3">
              <KeyTerm term="Terracing" definition="Cutting steps into mountains to create flat land for farming (East Asia rice, Andes potatoes). Reduces soil erosion but requires heavy labor." />
              <KeyTerm term="Irrigation" definition="Diverting water to crops. Can drain aquifers (e.g., Ogallala Aquifer) and shrink inland seas (e.g., Aral Sea)." />
              <KeyTerm term="Draining Wetlands" definition="Converts wetlands to farmland, destroying ecosystems and natural flood control." />
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-5-11",
      title: "Topic 5.11: Challenges of Contemporary Agriculture",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Gene Revolution (Biotech/GMOs)</h3>
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <p className="text-sm text-slate-700">Since the 1990s, scientists modify the DNA of crops (GMOs) to resist drought, pests, or chemicals (e.g., Roundup Ready seeds).</p>
              <div className="grid md:grid-cols-2 gap-2 mt-2">
                <p className="text-xs text-slate-600"><strong>✅ Pros:</strong> Higher yields, less pesticide use</p>
                <p className="text-xs text-slate-600"><strong>❌ Cons:</strong> Corporate control, unknown health effects, export resistance</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Aquaculture (The Blue Revolution)</h3>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <p className="text-sm text-slate-700">Farming of fish and shellfish.</p>
              <div className="grid md:grid-cols-2 gap-2 mt-2">
                <p className="text-xs text-slate-600"><strong>✅ Pros:</strong> Reliable protein, prevents overfishing</p>
                <p className="text-xs text-slate-600"><strong>❌ Cons:</strong> Disease, antibiotic overuse, waste pollution</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Sustainability Movements</h3>
            <div className="space-y-3">
              <KeyTerm term="Organic Farming" definition="Farming without synthetic fertilizers/pesticides. Growing market in MDCs." />
              <KeyTerm term="Fair Trade" definition="Movement to ensure producers in LDCs get a fair price and living wage (common with coffee/chocolate)." />
              <KeyTerm term="Eat Local / Farm-to-Table" definition="Reduces 'food miles' (carbon footprint of transport) and supports local economies." />
              <KeyTerm term="Community Supported Agriculture (CSA)" definition="Consumers buy a 'subscription' to a local farm's harvest." />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Food Access</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">🏙️ Food Deserts</h4>
                <p className="text-sm text-slate-700 mt-1">Urban neighborhoods or rural towns without ready access to fresh, healthy, and affordable food.</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">🍽️ Food Insecurity</h4>
                <p className="text-sm text-slate-700 mt-1">The state of being without reliable access to a sufficient quantity of affordable, nutritious food.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-5-12",
      title: "Topic 5.12: Women in Agriculture",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Role of Women</h3>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <ul className="text-sm text-slate-700 space-y-2">
                <li>Globally, women comprise about 43% of the agricultural labor force.</li>
                <li>In <strong>Sub-Saharan Africa</strong> and parts of South Asia, women produce up to 70% of the food (mostly in subsistence agriculture) while men often migrate to cities for work.</li>
              </ul>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Obstacles for Women</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Despite their role, women face systemic inequality:
            </p>
            <div className="space-y-3">
              <KeyTerm term="Land Rights" definition="Laws or customs often prevent women from owning land." />
              <KeyTerm term="Access to Credit" definition="Banks often require land titles for loans, excluding women." />
              <KeyTerm term="Technology" definition="Cultural norms often reserve machinery/training for men." />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Impact of Empowerment</h3>
            <ImportantNote>
              If women had the same access to productive resources as men, farm yields in developing countries could increase by 20–30%, significantly reducing global hunger.
            </ImportantNote>
          </section>
        </div>
      )
    }
  ]
};

export default humanGeographyUnit5Notes;